/**
 * Formatage des dates au format français.
 *
 * `Intl.DateTimeFormat` est natif côté navigateur (zéro coût bundle).
 * Centraliser ici évite la duplication entre ParametresMembres,
 * Dashboard, futurs écrans cuvées/tirages, etc.
 */

/**
 * Format court : "15 mars 2026". Tolère null/undefined/string ISO en
 * entrée (UI tables où la valeur peut être absente). Retourne "—"
 * pour les vides plutôt qu'une chaîne vide (lisibilité tableau).
 */
export function formatDateFr(date: Date | string | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Format numérique court : ISO jour pur `AAAA-MM-JJ` → `JJ/MM/AAAA`
 * (listes/fiches cuverie et vendange). "—" si vide, chaîne d'origine si
 * le format est inattendu (on n'invente pas de date).
 */
export function formatDateIsoFr(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [annee, mois, jour] = iso.split("-");
  if (!annee || !mois || !jour) return iso;
  return `${jour}/${mois}/${annee}`;
}

/**
 * Format long : "samedi 15 mars 2026". Pour les en-têtes "Aujourd'hui
 * c'est…" du dashboard, hero pages, etc.
 */
export function formatDateLongFr(date: Date): string {
  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/**
 * Date du jour au format jour pur "AAAA-MM-JJ" (aligné `dateJourSchema` back).
 * Valeur par défaut des champs `<input type="date">`.
 *
 * En heure de PARIS, pas en UTC : `toISOString()` renvoyait la date UTC, donc
 * entre minuit et 2 h (heure d'été) le formulaire se pré-remplissait avec la
 * VEILLE. Sur une pesée de vendange saisie à 1 h du matin, ça inscrit une date
 * fausse au registre. Même règle que `dateDuJourParis()` côté API — c'est la
 * date de l'horloge murale du vigneron qui fait foi.
 * Audit qualité 2026-07-25.
 */
export function aujourdhuiISO(maintenant: Date = new Date()): string {
  // La locale `en-CA` formate nativement en `AAAA-MM-JJ`.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(maintenant);
}
